import type {
  AnnualTaxAdjustment,
  AnnualTaxSettings,
  AppData,
  AuthUser,
  DocumentKind,
  DocumentScanDraft,
  DriverProfile,
  Expense,
  IncomeSummary,
  SourceDocument,
} from "@/types/tax";
import { defaultNotificationSettings, emptyProfile } from "@/data/defaults";
import type { SubscriptionCheckoutPlan } from "@/lib/plans";

export type SubscriptionPlanPrice = {
  amount: number;
  currency: string;
  interval: "day" | "week" | "month" | "year";
  intervalCount: number;
};

export type SubscriptionPlanPrices = Partial<Record<SubscriptionCheckoutPlan, SubscriptionPlanPrice>>;

type ApiResult<T> = { ok: true; data: T } | { ok: false; error: string };

export const AUTH_REQUIRED_EVENT = "rideshare-tax:auth-required";

let csrfToken = "";

function delay(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function normalizeProfile(profile: DriverProfile | null): DriverProfile {
  const merged = profile
    ? {
        ...emptyProfile,
        ...profile,
        plan: profile.plan || "free",
        manualPlan: profile.manualPlan || profile.plan || "free",
        planSource: profile.planSource || (profile.plan === "free" ? "free" : "manual"),
        billingManagementAvailable: Boolean(profile.billingManagementAvailable),
        scanQuotaLimit: Number(profile.scanQuotaLimit || 0),
        scanQuotaUsed: Number(profile.scanQuotaUsed || 0),
        scanQuotaRemaining: Number(profile.scanQuotaRemaining || 0),
        scanQuotaResetAt: profile.scanQuotaResetAt ?? null,
      }
    : { ...emptyProfile, id: `profile-${Date.now()}` };
  return {
    ...merged,
    notificationSettings: {
      ...defaultNotificationSettings,
      ...(profile?.notificationSettings || {}),
      timezone: profile?.notificationSettings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || defaultNotificationSettings.timezone,
    },
  };
}

async function request<T>(path: string, options: RequestInit = {}): Promise<ApiResult<T>> {
  try {
    const isFormData = options.body instanceof FormData;
    const response = await fetch(`api/${path}`, {
      credentials: "include",
      headers: {
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        ...(options.headers || {}),
      },
      ...options,
    });
    const payload = (await response.json()) as { ok?: boolean; data?: T; error?: string; csrfToken?: string };
    if (payload.csrfToken) csrfToken = payload.csrfToken;
    if (!response.ok || payload.ok === false) {
      if (response.status === 401 && payload.error === "Authentication required.") {
        csrfToken = "";
        window.dispatchEvent(new CustomEvent(AUTH_REQUIRED_EVENT));
      }
      return { ok: false, error: payload.error || "Request failed" };
    }
    const hasData = Object.prototype.hasOwnProperty.call(payload, "data");
    return { ok: true, data: (hasData ? payload.data : payload) as T };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : "Network error" };
  }
}

export async function getCurrentUser() {
  const result = await request<AuthUser | null>("auth.php?action=me", { method: "GET" });
  if (result.ok && result.data?.csrfToken) csrfToken = result.data.csrfToken;
  return result;
}

export async function login(email: string, password: string) {
  return request<AuthUser>("auth.php?action=login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

export async function register(email: string, password: string, displayName: string) {
  return request<AuthUser>("auth.php?action=register", {
    method: "POST",
    body: JSON.stringify({ email, password, displayName }),
  });
}

export async function logout() {
  return request<null>("auth.php?action=logout", { method: "POST", body: "{}" });
}

export async function fetchAppData(): Promise<ApiResult<AppData>> {
  let profile = await request<DriverProfile | null>("profile.php");
  let incomeSummaries = await request<IncomeSummary[]>("income.php");
  let documents = await request<SourceDocument[]>("documents.php");
  let expenses = await request<Expense[]>("expenses.php");
  let annualTaxSettings = await request<AnnualTaxSettings[]>("annual-tax.php?action=settings");
  let annualTaxAdjustments = await request<AnnualTaxAdjustment[]>("annual-tax.php?action=adjustments");

  if (!profile.ok || !incomeSummaries.ok || !documents.ok || !expenses.ok) {
    await delay(250);
    profile = await request<DriverProfile | null>("profile.php");
    incomeSummaries = await request<IncomeSummary[]>("income.php");
    documents = await request<SourceDocument[]>("documents.php");
    expenses = await request<Expense[]>("expenses.php");
    annualTaxSettings = await request<AnnualTaxSettings[]>("annual-tax.php?action=settings");
    annualTaxAdjustments = await request<AnnualTaxAdjustment[]>("annual-tax.php?action=adjustments");
  }

  if (!profile.ok || !incomeSummaries.ok || !documents.ok || !expenses.ok) {
    return { ok: false, error: "API unavailable. Check the database/API configuration and try again." };
  }

  return {
    ok: true,
    data: {
      profile: normalizeProfile(profile.data),
      incomeSummaries: incomeSummaries.data,
      documents: documents.data,
      expenses: expenses.data,
      annualTaxSettings: annualTaxSettings.ok ? annualTaxSettings.data : [],
      annualTaxAdjustments: annualTaxAdjustments.ok ? annualTaxAdjustments.data : [],
    },
  };
}

export async function saveProfile(profile: DriverProfile) {
  return request<DriverProfile>("profile.php", { method: "POST", body: JSON.stringify(profile) });
}

export async function getVapidPublicKey() {
  return request<{ publicKey: string }>("notifications.php?action=vapid-key");
}

export async function subscribeNotifications(subscription: PushSubscriptionJSON, timezone: string) {
  return request<{ enabled: boolean }>("notifications.php?action=subscribe", {
    method: "POST",
    body: JSON.stringify({ subscription, timezone }),
  });
}

export async function unsubscribeNotifications(endpoint?: string | null) {
  return request<{ enabled: boolean }>("notifications.php?action=unsubscribe", {
    method: "POST",
    body: JSON.stringify({ endpoint }),
  });
}

export async function createCheckoutSession(plan: SubscriptionCheckoutPlan = "pro") {
  return request<{ url: string }>("create-checkout.php", { method: "POST", body: JSON.stringify({ plan }) });
}

export async function getSubscriptionPlanPrices() {
  return request<SubscriptionPlanPrices>("subscription-plans.php");
}

export async function createCustomerPortalSession() {
  return request<{ url: string }>("customer-portal.php", { method: "POST", body: "{}" });
}

export async function saveDocument(document: SourceDocument, pdf?: File | null) {
  if (pdf) {
    const formData = new FormData();
    formData.append("document", JSON.stringify(document));
    formData.append("pdf", pdf);
    return request<SourceDocument>("documents.php", { method: "POST", body: formData });
  }
  return request<SourceDocument>("documents.php", { method: "POST", body: JSON.stringify(document) });
}

export async function removeDocumentPdf(id: string) {
  return request<SourceDocument[]>("documents.php", { method: "DELETE", body: JSON.stringify({ id, action: "delete_record" }) });
}

export function documentPreviewUrl(id: string) {
  return `api/documents.php?action=preview&id=${encodeURIComponent(id)}`;
}

export async function scanDocument(pdf: File, expectedKind: DocumentKind, localDraft?: Partial<DocumentScanDraft> | null) {
  const formData = new FormData();
  formData.append("pdf", pdf);
  formData.append("expectedKind", expectedKind);
  if (localDraft?.income) {
    formData.append("localDraft", JSON.stringify(localDraft));
  }
  return request<DocumentScanDraft>("documents.php?action=scan", { method: "POST", body: formData });
}

export async function saveIncomeSummary(income: IncomeSummary) {
  return request<IncomeSummary>("income.php", { method: "POST", body: JSON.stringify(income) });
}

export async function deleteIncomeSummary(id: string) {
  return request<IncomeSummary[]>("income.php", { method: "DELETE", body: JSON.stringify({ id }) });
}

export async function saveExpense(expense: Expense) {
  return request<Expense>("expenses.php", { method: "POST", body: JSON.stringify(expense) });
}

export async function deleteExpense(id: string) {
  return request<Expense[]>("expenses.php", { method: "DELETE", body: JSON.stringify({ id }) });
}

export async function saveAnnualTaxSettings(settings: AnnualTaxSettings) {
  return request<AnnualTaxSettings>("annual-tax.php?action=settings", { method: "POST", body: JSON.stringify(settings) });
}

export async function saveAnnualTaxAdjustment(adjustment: AnnualTaxAdjustment) {
  return request<AnnualTaxAdjustment>("annual-tax.php?action=adjustment", { method: "POST", body: JSON.stringify(adjustment) });
}

export async function deleteAnnualTaxAdjustment(id: string) {
  return request<AnnualTaxAdjustment[]>("annual-tax.php?action=adjustment", { method: "DELETE", body: JSON.stringify({ id }) });
}
